import convert from 'convert-units'
import { computeDistanceBetween } from 'spherical-geometry-js'

import type { SiteRestaurantLocation } from 'shared/js/types'

export type supportedRangeUnits = 'mi' | 'km'

export type LocationAddress = Pick<
  Partial<SiteRestaurantLocation>,
  'name' | 'address1' | 'address2' | 'city' | 'state' | 'zipcode'
>

export type LocationWithDistance = SiteRestaurantLocation & {
  distanceToReference: number
  distanceUnits: supportedRangeUnits
}

export type LocationCoordinates = {
  lat: number
  lng: number
}

export const formatLocationAddress = (
  location: SiteRestaurantLocation | null
): string => {
  if (!location) return ''

  const parts: string[] = []

  if (location.address1) parts.push(location.address1)
  if (location.address2) parts.push(location.address2)

  const cityStateZip: string[] = []
  if (location.city) cityStateZip.push(location.city)
  if (location.state) cityStateZip.push(location.state)
  if (location.zipcode) cityStateZip.push(location.zipcode)

  if (cityStateZip.length > 0) {
    parts.push(cityStateZip.join(' '))
  }

  return parts.join(', ')
}

// Sort locations consistently by name, then by externalId
export const sortLocations = (
  locations: SiteRestaurantLocation[] | null | undefined
): SiteRestaurantLocation[] => {
  if (!locations || locations.length === 0) return []
  return [...locations].sort((a, b) => {
    const nameComparison = (a?.name ?? '').localeCompare(b?.name ?? '')
    if (nameComparison !== 0) return nameComparison
    return (a?.externalId ?? '').localeCompare(b?.externalId ?? '')
  })
}

/**
 * Calculate distance between two coordinates and return appropriate units
 */
export const calculateLocationDistance = (
  location: SiteRestaurantLocation,
  referenceCoords: LocationCoordinates
): { distance: number; units: supportedRangeUnits } => {
  //If location coordinates are missing, return -1 distance
  if (location.lat == null || location.long == null) {
    const units: supportedRangeUnits =
      location.country && location.country !== 'US' && location.country !== 'GB'
        ? 'km'
        : 'mi'
    return { distance: -1, units }
  }

  const distanceMeters = computeDistanceBetween(
    { lat: location.lat, long: location.long },
    { lat: referenceCoords.lat, long: referenceCoords.lng }
  )

  // Use miles in the US and UK, kilometers everywhere else
  // Based on: https://github.toasttab.com/toasttab/library-localization-reference/blob/main/library-localization-reference/src/main/kotlin/com/toasttab/common/localization/reference/data/Countries.kt
  const units: supportedRangeUnits =
    location.country && location.country !== 'US' && location.country !== 'GB'
      ? 'km'
      : 'mi'

  const distance = convert(distanceMeters).from('m').to(units)

  return { distance, units }
}

/**
 * Add distance calculations to locations array
 */
export const addDistanceToLocations = (
  locations: SiteRestaurantLocation[] | null | undefined,
  referenceCoords: LocationCoordinates | null
): LocationWithDistance[] => {
  if (!locations || locations.length === 0) return []
  if (!referenceCoords) {
    // Return locations with -1 distance if no reference coordinates
    return locations.map((loc) => ({
      ...loc,
      distanceToReference: -1,
      distanceUnits: (loc.country &&
      loc.country !== 'US' &&
      loc.country !== 'GB'
        ? 'km'
        : 'mi') as supportedRangeUnits
    }))
  }

  return locations.map((loc) => {
    const { distance, units } = calculateLocationDistance(loc, referenceCoords)
    return {
      ...loc,
      distanceToReference: distance,
      distanceUnits: units
    }
  })
}

/**
 * Sort locations by distance (closest first), fallback to name sorting
 */
export const sortLocationsByDistance = (
  locations: LocationWithDistance[]
): LocationWithDistance[] => {
  return [...locations].sort((a, b) => {
    // If both have valid distances, sort by distance
    if (a.distanceToReference >= 0 && b.distanceToReference >= 0) {
      return a.distanceToReference - b.distanceToReference
    }

    // If neither has distance, sort by name
    if (a.distanceToReference < 0 && b.distanceToReference < 0) {
      const nameComparison = (a?.name ?? '').localeCompare(b?.name ?? '')
      if (nameComparison !== 0) return nameComparison
      return (a?.externalId ?? '').localeCompare(b?.externalId ?? '')
    }

    // Prioritize locations with valid distances
    if (a.distanceToReference >= 0) return -1
    if (b.distanceToReference >= 0) return 1

    return 0
  })
}
